/**
 * Fordonskort — fliken Egenskaper (Car.info-specifikationer) med fri textsökning.
 */

import React, { useMemo, useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';

const BASIC_CATEGORY_NAME = 'Grund information (Car.info)';

const PRIORITY_CATEGORIES = [
  BASIC_CATEGORY_NAME,
  'Exteriör',
  'Vikt',
  'Prestanda',
  'Motorspecifikationer',
  'Drivning',
  'Utsläpp WLTP',
  'Bränsleförbrukning NEDC',
];

interface SpecAttribute {
  id: string | number;
  name?: string;
  values?: unknown;
  unit?: string;
}

interface SpecCategory {
  category_id: string | number;
  category_name: string;
  attributes?: Record<string, SpecAttribute>;
}

interface ExtendedAttr {
  id?: string | number;
  name?: string;
  unit?: string;
  values?: unknown[];
}

const EXTENDED_CATEGORY_ID = '__extended_attributes__';
const BASIC_CATEGORY_ID = '__basic_attributes__';

interface VehicleSpecificationsTabProps {
  specifications: SpecCategory[] | null | undefined;
  /** Car.info grundattribut (toppnivåfält + ägarorgnr) – visas som egen sökbar kategori överst. */
  basicAttributes?: ExtendedAttr[] | null | undefined;
  /** Car.info extended_attributes – visas som en egen sökbar kategori. */
  extendedAttributes?: ExtendedAttr[] | null | undefined;
  expandedCategories: Set<string>;
  onToggleCategory: (categoryId: string) => void;
}

function buildCategoryFromAttrs(
  attrs: ExtendedAttr[] | null | undefined,
  categoryId: string,
  categoryName: string
): SpecCategory | null {
  const valid = (attrs ?? []).filter(
    (a) =>
      a?.name &&
      String(a.name).trim() !== '' &&
      a?.values != null &&
      (!Array.isArray(a.values) || a.values.length > 0)
  );
  if (valid.length === 0) return null;
  const attributes: Record<string, SpecAttribute> = {};
  valid.forEach((a, idx) => {
    const key = String(a.id ?? `${categoryId}_${idx}`);
    attributes[key] = { id: a.id ?? key, name: a.name, values: a.values, unit: a.unit };
  });
  return { category_id: categoryId, category_name: categoryName, attributes };
}

function formatAttrDisplay(attr: SpecAttribute): string {
  const raw = Array.isArray(attr.values) ? attr.values.join(', ') : String(attr.values ?? '');
  const unit = attr.unit && attr.unit !== 'n/a' ? ` ${attr.unit}` : '';
  return `${raw}${unit}`.trim();
}

function attrSearchText(attr: SpecAttribute): string {
  return `${attr.name ?? ''} ${formatAttrDisplay(attr)}`.toLowerCase();
}

export const VehicleSpecificationsTab: React.FC<VehicleSpecificationsTabProps> = ({
  specifications,
  basicAttributes,
  extendedAttributes,
  expandedCategories,
  onToggleCategory,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const isSearching = normalizedQuery.length > 0;

  // Slå ihop specifications med Car.info grundattribut + extended_attributes
  // (som egna kategorier), så att ALL data vi hämtar från Car.info blir sökbar under Egenskaper.
  const allSpecs = useMemo<SpecCategory[]>(() => {
    const specs = [...(specifications ?? [])];

    const basicCat = buildCategoryFromAttrs(
      basicAttributes,
      BASIC_CATEGORY_ID,
      t('vehicleCard.propertiesBasicCategory') || BASIC_CATEGORY_NAME
    );
    if (basicCat) specs.push(basicCat);

    const extCat = buildCategoryFromAttrs(
      extendedAttributes,
      EXTENDED_CATEGORY_ID,
      t('vehicleCard.propertiesExtendedCategory') || 'Övriga uppgifter (Car.info)'
    );
    if (extCat) specs.push(extCat);

    return specs;
  }, [specifications, basicAttributes, extendedAttributes, t]);

  const sortedSpecs = useMemo(() => {
    const specs = allSpecs;
    return [...specs].sort((a, b) => {
      const aIdx = PRIORITY_CATEGORIES.indexOf(a.category_name);
      const bIdx = PRIORITY_CATEGORIES.indexOf(b.category_name);
      if (aIdx === -1 && bIdx === -1) return 0;
      if (aIdx === -1) return 1;
      if (bIdx === -1) return -1;
      return aIdx - bIdx;
    });
  }, [allSpecs]);

  const filteredCategories = useMemo(() => {
    return sortedSpecs
      .map((category) => {
        const attrList = Object.values(category.attributes ?? {}) as SpecAttribute[];
        const visibleAttrs = isSearching
          ? attrList.filter((attr) => attrSearchText(attr).includes(normalizedQuery))
          : attrList;
        return { category, visibleAttrs };
      })
      .filter(({ visibleAttrs }) => !isSearching || visibleAttrs.length > 0);
  }, [sortedSpecs, isSearching, normalizedQuery]);

  const totalMatchCount = useMemo(
    () => filteredCategories.reduce((sum, { visibleAttrs }) => sum + visibleAttrs.length, 0),
    [filteredCategories]
  );

  if (allSpecs.length === 0) {
    return (
      <div className="text-textSecondary text-center py-8">{t('vehicleCard.noSpecifications')}</div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="relative max-w-xl">
        <MagnifyingGlassIcon
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-textTertiary pointer-events-none"
          aria-hidden
        />
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('vehicleCard.propertiesSearchPlaceholder')}
          className="w-full pl-9 pr-9 py-2 text-sm border border-border rounded-lg bg-bgLight text-textDefault placeholder:text-textTertiary focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label={t('vehicleCard.propertiesSearchPlaceholder')}
        />
        {searchQuery.trim() !== '' && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-textSecondary hover:text-textDefault hover:bg-bgBase"
            title={t('vehicleCard.propertiesSearchClear')}
            aria-label={t('vehicleCard.propertiesSearchClear')}
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {isSearching && (
        <p className="text-xs text-textSecondary">
          {totalMatchCount === 0
            ? t('vehicleCard.propertiesSearchNoResults')
            : t('vehicleCard.propertiesSearchMatchCount', { count: totalMatchCount })}
        </p>
      )}

      {isSearching && totalMatchCount === 0 ? null : (
        <div className="space-y-2">
          {filteredCategories.map(({ category, visibleAttrs }) => {
            const categoryId = String(category.category_id);
            const isExpanded = isSearching || expandedCategories.has(categoryId);

            return (
              <div key={categoryId} className="border border-border rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => onToggleCategory(categoryId)}
                  className="w-full px-3 py-2 bg-bgBase hover:bg-bgLight flex justify-between items-center text-left"
                >
                  <span className="text-sm font-medium text-textDefault">{category.category_name}</span>
                  <span className="text-xs text-textSecondary">
                    {isSearching
                      ? t('vehicleCard.propertiesSearchMatchesInCategory', { count: visibleAttrs.length })
                      : t('vehicleCard.propertiesAttributeCount', { count: visibleAttrs.length })}{' '}
                    {isExpanded ? '▲' : '▼'}
                  </span>
                </button>
                {isExpanded && (
                  <div className="px-3 py-2 bg-bgElevated">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
                      {visibleAttrs.map((attr) => (
                        <div
                          key={String(attr.id)}
                          className="flex justify-between gap-2 py-0.5 border-b border-border"
                        >
                          <span className="text-textSecondary shrink-0">{attr.name}:</span>
                          <span className="text-textDefault font-medium text-right break-words">
                            {formatAttrDisplay(attr) || '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VehicleSpecificationsTab;
